import { gql } from '@apollo/client'
import {
	DIRECT_UPLOAD_FRAGMENT,
	IMAGE_FILE_FRAGMENT,
	WORK_EXPERIENCE_FRAGMENT,
	WORKED_TIMESHEET_FRAGMENT,
	WORKER_ADDRESS_FRAGMENT,
	WORKER_CERTIFICATION_FRAGMENT,
} from '../apollo.fragment'
import { WORKER_FRAGMENT } from '../fragments'

export const SIGNIN_WORKER_MUTATION = gql`
	mutation signInWorker($recaptchaToken: String, $email: String!, $password: String!, $socialAuthenticationId: String) {
		signInWorker(recaptchaToken: $recaptchaToken, email: $email, password: $password, socialAuthenticationId: $socialAuthenticationId) {
			authToken {
				accessToken
				expiresIn
				refreshToken
				tokenType
			}
			notificationChannel
			worker {
				...workerFragment
			}
		}
	}
	${WORKER_FRAGMENT}
`

export const VERIFY_OAUTH_LOGIN = gql`
	mutation verifyOauthLogin(
		$providerName: SocialProviderEnum!
		$clientId: String!
		$clientToken: String
		$email: String
		$code: String
		$redirectUri: String
		$recaptchaToken: String
	) {
		verifySocialAuthentication(
			providerName: $providerName
			clientId: $clientId
			clientToken: $clientToken
			email: $email
			code: $code
			redirectUri: $redirectUri
			recaptchaToken: $recaptchaToken
		) {
			authToken {
				accessToken
				refreshToken
			}
			emailHasAccount
			userSocialAuthentication {
				email
				providerName
				id
				providerUuid
				userId
			}
			worker {
				email
				firstName
				id
				lastName
				phone
				phoneVerifiedAt
				user {
					changePasswordRequired
				}
			}
		}
	}
`

export const CREATE_WORKER_MUTATION = gql`
	mutation(
		$recaptchaToken: String
		$firstName: String
		$lastName: String
		$email: String
		$password: String
		$socialAuthenticationId: String
		$candidateType: String
	) {
		signUpWorker(
			recaptchaToken: $recaptchaToken
			firstName: $firstName
			lastName: $lastName
			email: $email
			password: $password
			socialAuthenticationId: $socialAuthenticationId
			candidateType: $candidateType
		) {
			authToken {
				accessToken
				refreshToken
				tokenType
				expiresIn
			}
			worker {
				id
				lastName
				firstName
				email
				initialScreeningResult
				user {
					changePasswordRequired
				}
				fromOfferAcceptedJobIds
			}
			notificationChannel
		}
	}
`

export const UPDATE_WORKER_MUTATION = gql`
	mutation updateWorker($id: String!, $referral: String!) {
		updateIntegrationWorker(id: $id, referralMethod: $referral) {
			id
		}
	}
`

export const UPDATE_WORKER_LOCATION_MUTATION = gql`
	mutation createWorkingPreferredLocations($id: String!, $location1: String, $location2: String, $location3: String) {
		createWorkingPreferredLocations(
			id: $id
			workingPreferredLocationsAttributes: [{ preferredWorkingState: $location1 }, { preferredWorkingState: $location2 }, { preferredWorkingState: $location3 }]
		) {
			success
		}
	}
`

export const SEND_PASSWORD_RESET_WORKER_MUTATION = gql`
	mutation sendPasswordResetWorker($email: String!, $recaptchaToken: String!) {
		sendPasswordResetWorker(email: $email, recaptchaToken: $recaptchaToken) {
			success
		}
	}
`

export const RESET_PASSWORD_WORKER_MUTATION = gql`
	mutation resetPasswordForWorker($email: String!, $token: String!, $password: String!, $passwordConfirmation: String!, $recaptchaToken: String!) {
		resetPasswordForWorker(email: $email, token: $token, password: $password, passwordConfirmation: $passwordConfirmation, recaptchaToken: $recaptchaToken) {
			success
		}
	}
`

export const UPDATE_WORK_EXPERIENCE_MUTATION = gql`
	mutation updateWorkExperience($id: String!, $experiences: [WorkExperienceArgument!]) {
		updateIntegrationWorker(id: $id, workExperiencesAttributes: $experiences) {
			workExperiences {
				...workExperienceFragment
			}
		}
	}
	${WORK_EXPERIENCE_FRAGMENT}
`

export const UPDATE_WORKER_LICENSE_MUTATION = gql`
	mutation updateWorkerLicenses($id: String!, $licenses: [WorkerCertificationArgument!]) {
		updateIntegrationWorker(id: $id, workerCertificationsAttributes: $licenses) {
			id
			workerCertifications {
				...workerCertificationFragment
			}
		}
	}

	${WORKER_CERTIFICATION_FRAGMENT}
`

export const CREATE_DIRECT_UPLOAD_MUTATION = gql`
	mutation createDirectUpload($filename: String!, $byteSize: Int!, $checksum: String!, $contentType: String!) {
		createDirectUpload(filename: $filename, byteSize: $byteSize, checksum: $checksum, contentType: $contentType) {
			...directUploadFragment
		}
	}
	${DIRECT_UPLOAD_FRAGMENT}
`

export const CREATE_WORKED_TIMESHEET_MUTATION = gql`
	mutation createWorkedTimesheet($timesheetImagesSignedBlobIds: [String!]!) {
		createWorkedTimesheet(timesheetImagesSignedBlobIds: $timesheetImagesSignedBlobIds, source: opus_web_portal) {
			...workedTimesheetFragment
			timesheetImages {
				...imageFileFragment
			}
		}
	}
	${WORKED_TIMESHEET_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`

export const DESTROY_FILE_MUTATION = gql`
	mutation destroyDocument($id: String!) {
		destroyDocument(id: $id) {
			id
			success
		}
	}
`

export const UPDATE_WORKED_TIMESHEET_MUTATION = gql`
	mutation updateWorkedTimesheet($id: String!, $signedBlobId: [String!]!) {
		updateWorkedTimesheet(id: $id, signedBlobId: $signedBlobId) {
			...workedTimesheetFragment
			timesheetImages {
				...imageFileFragment
			}
		}
	}
	${WORKED_TIMESHEET_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`
export const UPDATE_WORKER_RESUME_MUTATION = gql`
	mutation($resumesSignedBlobIds: [String!]) {
		createOrUpdateWorkerResumes(resumesSignedBlobIds: $resumesSignedBlobIds) {
			resumes {
				id
				filename
				fileUrl
				contentType
				blobId
				imageUrls(size: ["100x100", "200x200", "500x500"])
			}
		}
	}
`

export const SEND_OTP_CODE_MUTATION = gql`
	mutation sendOtpCodeMutation($phoneNumber: String!) {
		sendOtpCode(phoneNumber: $phoneNumber) {
			success
			worker {
				remainingExpirationTime
			}
		}
	}
`

export const VERIFY_OTP_CODE_MUTATION = gql`
	mutation verifyOtpCodeMutation($otpCode: String!, $phoneNumber: String!) {
		verifyOtpCode(otpCode: $otpCode, phoneNumber: $phoneNumber) {
			success
		}
	}
`

export const UPDATE_WORKER_PREFER_LOCATION_MUTATION = gql`
	mutation UpdateWorkingPreferredLocations($workingPreferredLocationsAttributes: [WorkingPreferredLocationArgument!]) {
		updateWorkingPreferredLocations(workingPreferredLocationsAttributes: $workingPreferredLocationsAttributes) {
			id
			preferredWorkingCity
			preferredWorkingState
		}
	}
`
export const UPDATE_WORKER_INFO_MUTATION = gql`
	mutation(
		$firstName: String
		$lastName: String
		$email: String
		$facilityTypes: [String!]
		$shiftRequested: [String!]
		$availableStartDate: ISO8601Date
		$workingPreferredLocationsAttributes: [WorkingPreferredLocationArgument!]
		$workerAddressAttributes: WorkerAddressArgument
		$workerSpecialtiesAttributes: [WorkerSpecialtyArgument!]
		$phone: String
	) {
		updateWorker(
			firstName: $firstName
			lastName: $lastName
			email: $email
			facilityTypes: $facilityTypes
			shiftRequested: $shiftRequested
			availableStartDate: $availableStartDate
			workingPreferredLocationsAttributes: $workingPreferredLocationsAttributes
			workerAddressAttributes: $workerAddressAttributes
			workerSpecialtiesAttributes: $workerSpecialtiesAttributes
			phone: $phone
		) {
			...workerFragment
			workerAddress {
				...workerAddressFragment
			}
			workingPreferredLocations {
				id
				preferredWorkingState
			}
		}
	}
	${WORKER_FRAGMENT}
	${WORKER_ADDRESS_FRAGMENT}
`

export const CREATE_WORKER_CERTICATION_MUTATION = gql`
	mutation createWorkerCertification(
		$licenseSpecialty: String!
		$licenseState: String!
		$licenseNumber: String!
		$compactLicense: Boolean!
		$expirationDate: String!
		$certificationType: String!
		$licenseImages: [FileArgument!]!
	) {
		createWorkerCertification(
			licenseSpecialty: $licenseSpecialty
			licenseState: $licenseState
			licenseNumber: $licenseNumber
			compactLicense: $compactLicense
			expirationDate: $expirationDate
			certificationType: $certificationType
			licenseImages: $licenseImages
		) {
			...workerCertificationFragment
			licenseImages {
				...imageFileFragment
			}
		}
	}

	${WORKER_CERTIFICATION_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`
export const UPDATE_SHOW_PARTIAL_JOBS = gql`
	mutation updateIntegrationWorker($updateIntegrationWorkerId: String!, $settings: JSON) {
		updateIntegrationWorker(id: $updateIntegrationWorkerId, settings: $settings) {
			id
			settings
		}
	}
`

export const DISCONNECT_SOCIAL_AUTHENTICATION = gql`
	mutation DisconnectSocialAuthentication($socialAuthenticationId: String!) {
		disconnectSocialAuthentication(socialAuthenticationId: $socialAuthenticationId) {
			success
		}
	}
`
export const VERIFY_EXISTED_EMAIL = gql`
	mutation VerifyExistedEmail($email: String!, $recaptchaToken: String) {
		verifyExistedEmail(email: $email, recaptchaToken: $recaptchaToken) {
			existed
		}
	}
`
export const SIGN_UP_WORKER_MUTATION = gql`
	mutation CreateIntegrationWorker(
		$firstName: String!
		$lastName: String!
		$email: String!
		$phone: String!
		$password: String!
		$passwordConfirmation: String!
		$recaptchaToken: String
		$referrerCode: String
	) {
		createIntegrationWorker(
			firstName: $firstName
			lastName: $lastName
			email: $email
			phone: $phone
			password: $password
			passwordConfirmation: $passwordConfirmation
			recaptchaToken: $recaptchaToken
			referrerCode: $referrerCode
		) {
			authToken
			notificationChannel
			worker {
				phoneVerifiedAt
				firstName
				lastName
				id
				email
				phone
				fromOfferAcceptedJobIds
			}
		}
	}
`

export const SUPPORT_DOCUMENTS_MUTATION = gql`
	query {
		supportDocuments {
			id
			documentType
			document {
				id
				fileUrl
				filename
				contentType
				blobId
				thumbnails
				imageUrls(size: ["100x100", "200x200", "500x500"])
			}
		}
	}
`

export const CREATE_OR_UPDATE_SUPPORT_DOCUMENTS_MUTATION = gql`
	mutation($supportDocumentsAttributes: [SupportDocumentArgument!]!) {
		createOrUpdateSupportDocument(supportDocumentsAttributes: $supportDocumentsAttributes) {
			id
			documentType
			document {
				id
				fileUrl
				filename
				contentType
				contentType
				blobId
				thumbnails
				imageUrls(size: ["100x100", "200x200", "500x500"])
			}
		}
	}
`

export const DELETE_WORKER_SPECIALTY = gql`
	mutation($id: String!) {
		destroyWorkerSpecialty(id: $id) {
			success
			id
		}
	}
`

export const CREATE_OR_UPDATE_WORKER_SPECIALTY = gql`
	mutation($workerSpecialtiesAttributes: [WorkerSpecialtyArgument!]!) {
		createOrUpdateWorkerSpecialty(workerSpecialtiesAttributes: $workerSpecialtiesAttributes) {
			id
			specialty
			completedChecklist
			category
			isPrimary
			specialtyLongName
			workerSkillChecklistSections {
				id
				name
				workerSkillChecklists {
					id
					question
					proficiency
					frequency
				}
			}
		}
	}
`

export const GET_WORKER_SPECIALTIES_QUERY = gql`
	query {
		workerSpecialties {
			id
			specialty
			completedChecklist
			isPrimary
			category
			specialtyLongName
			workerSkillChecklistSections {
				id
				name
				workerSkillChecklists {
					id
					question
					proficiency
					frequency
				}
			}
		}
	}
`

export const UPDATE_WORKER_PHONE = gql`
	mutation UpdateWorkerPhone($phone: String) {
		updateWorker(phone: $phone) {
			phone
			phoneVerifiedAt
		}
	}
`
export const UPDATE_WORKER_AVATAR = gql`
	mutation updateWorkerAvatar($avatar: DocumentInput) {
		updateProfile(avatar: $avatar) {
			avatar {
				file {
					id
					fileUrl
					imageUrls(size: ["100x100", "200x200", "500x500"])
				}
			}
		}
	}
`

export const UPDATE_WORKER_NO_EMAIL = gql`
	mutation updateWorkerNoEmail($noEmail: Boolean) {
		updateWorker(noEmail: $noEmail) {
			noEmail
		}
	}
`

export const UPDATE_WORKER_PASSWORD = gql`
	mutation($password: String!) {
		updateProfile(password: $password) {
			id
		}
	}
`

export const REFRESH_WORKER_TOKEN = gql`
	mutation($refreshToken: String!) {
		refreshToken(refreshToken: $refreshToken) {
			authToken {
				accessToken
				refreshToken
				tokenType
				expiresIn
			}
		}
	}
`
export const GET_WORKER_NO_EMAIL = gql`
	query getWorkerNoEMAIL($workerId: String!) {
		worker(id: $workerId) {
			noEmail
		}
	}
`

export const JOB_FUNCTIONS_QUERY = gql`
	query($filter: SelectOptionFilter!) {
		selectOptions(filter: $filter) {
			nodes {
				id
				name
			}
			totalCount
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
		}
	}
`

export const CONFIRM_START_WORK_MUTATION = gql`
	mutation($hasStartedWork: Boolean!, $projectedStartDate: ISO8601Date) {
		confirmStartWork(hasStartedWork: $hasStartedWork, projectedStartDate: $projectedStartDate) {
			id
		}
	}
`

export const UPDATE_WORKER_PROFILE_MUTATION = gql`
	mutation UpdateWorkerProfile(
		$firstName: String
		$lastName: String
		$middleName: String
		$phone: String
		$resumeDescription: String
		$addresses: [AddressInput!]
		$workExperiences: [WorkExperienceInput!]
		$workerEducations: [WorkerEducationInput!]
	) {
		updateProfile(
			firstName: $firstName
			lastName: $lastName
			middleName: $middleName
			resumeDescription: $resumeDescription
			phone: $phone
			addresses: $addresses
			workExperiences: $workExperiences
			workerEducations: $workerEducations
		) {
			id
		}
	}
`

export const UPDATE_WORKER_RESUMES_MUTATION = gql`
	mutation($resumes: [DocumentInput!]) {
		updateProfile(resumes: $resumes) {
			id
			resumes {
				id
				file {
					id
					filename
					fileUrl
					blobId
					byteSize
					contentType
					imageUrls(size: ["100x100", "200x200", "500x500"])
				}
			}
		}
	}
`
